import React, { createContext, useState, useEffect } from "react";
import toast from "react-hot-toast";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Step 1: Load cart from localStorage when the app starts
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (err) {
      console.error("Error loading cart from localStorage", err);
      return [];
    }
  });

  // Step 2: Load orderMode from sessionStorage
  const [orderMode, setOrderMode] = useState(() => {
    try {
      const savedMode = sessionStorage.getItem("orderMode");
      return savedMode !== null ? JSON.parse(savedMode) : null;
    } catch (err) {
      console.error("Error loading orderMode from sessionStorage", err);
      return null;
    }
  });

  // Step 2.5: Load userDetails from sessionStorage
  const [userDetails, setUserDetails] = useState(() => {
    try {
      const savedDetails = sessionStorage.getItem("userDetails");
      return savedDetails ? JSON.parse(savedDetails) : { name: "", phone: "", address: "", tableNo: "" };
    } catch (err) {
      console.error("Error loading userDetails from sessionStorage", err);
      return { name: "", phone: "", address: "", tableNo: "" };
    }
  });

  // Step 3: Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cart));
    } catch (err) {
      console.error("Error saving cart to localStorage", err);
    }
  }, [cart]);

  // Step 4: Save orderMode and userDetails to sessionStorage whenever they change
  useEffect(() => {
    try {
      if (orderMode !== null) {
        sessionStorage.setItem("orderMode", JSON.stringify(orderMode));
      } else {
        sessionStorage.removeItem("orderMode");
      }
    } catch (err) {
      console.error("Error saving orderMode to sessionStorage", err);
    }
  }, [orderMode]);

  useEffect(() => {
    try {
      sessionStorage.setItem("userDetails", JSON.stringify(userDetails));
    } catch (err) {
      console.error("Error saving userDetails to sessionStorage", err);
    }
  }, [userDetails]);

  const clearOrderDetails = () => {
    setOrderMode(null);
    setUserDetails({ name: "", phone: "", address: "", tableNo: "" });
    sessionStorage.removeItem("orderMode");
    sessionStorage.removeItem("userDetails");
  };


  const addToCart = (product) => {
    if (product.stock_control === 0 && product.stock_quantity <= 0) {
      toast.error(`"${product.product_name}" is out of stock!`);
      return;
    }

    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        if (existingItem.stock_control === 0 && existingItem.qty >= existingItem.stock_quantity) {
          toast.error(`Only ${existingItem.stock_quantity} ${existingItem.unit || 'units'} available in stock for "${existingItem.product_name}"!`);
          return prevCart;
        }
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, qty: 1 }];
    });
  };

  const increaseQty = (id) => {
    setCart((prev) => {
      const item = prev.find(i => i.id === id);
      if (item && item.stock_control === 0 && item.qty >= item.stock_quantity) {
        toast.error(`Only ${item.stock_quantity} ${item.unit || 'units'} available in stock for "${item.product_name}"!`);
        return prev;
      }
      return prev.map((item) =>
        item.id === id ? { ...item, qty: item.qty + 1 } : item
      );
    });
  };

  const decreaseQty = (id) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.id === id
            ? { ...item, qty: item.qty - 1 }
            : item
        )
        .filter((item) => item.qty > 0) // Remove item if qty becomes 0
    );
  };

  const removeItem = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const updateQty = (id, value) => {
    // Allow empty string or just a decimal point while typing
    if (value === "" || value === ".") {
      setCart((prev) =>
        prev.map((i) => (i.id === id ? { ...i, qty: value } : i))
      );
      return;
    }

    const numericValue = parseFloat(value);

    setCart((prev) => {
      const item = prev.find((i) => i.id === id);
      if (!item) return prev;

      // Allow typing 0.xxx
      if (value === "0" || value === "0.") {
         return prev.map((i) => (i.id === id ? { ...i, qty: value } : i));
      }

      if (isNaN(numericValue) || numericValue < 0) {
        return prev;
      }

      if (item.stock_control === 0 && numericValue > item.stock_quantity) {
        toast.error(`Only ${item.stock_quantity} ${item.unit || 'units'} available in stock for "${item.product_name}"!`);
        return prev.map((i) =>
          i.id === id ? { ...i, qty: item.stock_quantity } : i
        );
      }

      return prev.map((i) => (i.id === id ? { ...i, qty: numericValue } : i));
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("cart"); // Clear from storage after order
    clearOrderDetails();
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        increaseQty,
        decreaseQty,
        removeItem,
        updateQty,
        clearCart,
        orderMode,
        setOrderMode,
        userDetails,
        setUserDetails,
        clearOrderDetails,
      }}
    >
      {children}
    </CartContext.Provider>


  );
};